/**
 * Serielle Konsole eines Servers.
 *
 * Die Eingabe geht als Kommando an den Server und wird dort im
 * Simulationskern ausgeführt – gegen den aktuellen Weltzustand, nicht gegen die
 * Kopie im Browser, die zwischen zwei Aktionen veraltet. Reine Abfragen kommen
 * still zurück; nur was etwas ändert (SYN-Cookies, Paketfilter, Neustart),
 * landet im Protokoll.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import clsx from 'clsx';
import { CONSOLE_MAX_LINE, type Server } from '@hosting-sim/core';
import { useGame } from '../store/game';

interface Entry {
  id: number;
  input: string | null;
  output: string[];
  error?: boolean;
}

/** Häufige Befehle als Abkürzung – wer kein Linux tippt, soll trotzdem nachsehen können. */
const SUGGESTIONS = ['help', 'uptime', 'iftop', 'ss -s', 'tcpdump -c 15', 'dmesg', 'tail /var/log/nginx/access.log', 'iptables -L -n'];

let entryCounter = 0;

export function ServerConsole({ server, className }: { server: Server; className?: string }) {
  const run = useGame((s) => s.run);
  const [entries, setEntries] = useState<Entry[]>(() => banner(server));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [cursor, setCursor] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Ein anderer Server ist eine andere Sitzung.
  useEffect(() => {
    setEntries(banner(server));
    setHistory([]);
    setCursor(null);
  }, [server.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [entries]);

  const prompt = `root@${server.name}:~#`;

  async function submit(line: string) {
    const trimmed = line.trim();
    setInput('');
    setCursor(null);
    if (!trimmed) {
      setEntries((list) => [...list, { id: ++entryCounter, input: '', output: [] }]);
      return;
    }
    setHistory((list) => (list[list.length - 1] === trimmed ? list : [...list, trimmed].slice(-50)));
    setBusy(true);
    const result = await run({ type: 'server/console', serverId: server.id, line: trimmed }, { quiet: true });
    setBusy(false);

    if (result.clear) {
      setEntries([]);
    } else {
      const output = result.output ?? (result.message ? [result.message] : []);
      setEntries((list) => [...list, { id: ++entryCounter, input: trimmed, output, error: !result.ok }].slice(-200));
    }
    if (result.close) {
      setEntries((list) => [...list, { id: ++entryCounter, input: null, output: ['Verbindung getrennt. Enter öffnet eine neue Sitzung.'] }]);
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!busy) void submit(input);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (history.length === 0) return;
      const next = cursor === null ? history.length - 1 : Math.max(0, cursor - 1);
      setCursor(next);
      setInput(history[next]);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (cursor === null) return;
      const next = cursor + 1;
      if (next >= history.length) {
        setCursor(null);
        setInput('');
      } else {
        setCursor(next);
        setInput(history[next]);
      }
    } else if (event.key === 'l' && event.ctrlKey) {
      event.preventDefault();
      setEntries([]);
    }
  }

  return (
    <div className={clsx('space-y-2', className)}>
      <div
        ref={scrollRef}
        onClick={() => inputRef.current?.focus()}
        className="h-80 overflow-y-auto rounded-md border border-zinc-800 bg-zinc-950 p-3 font-mono text-xs leading-relaxed text-zinc-200"
      >
        {entries.map((entry) => (
          <div key={entry.id}>
            {entry.input !== null && (
              <div className="whitespace-pre-wrap break-all">
                <span className="text-emerald-400">{prompt}</span> {entry.input}
              </div>
            )}
            {entry.output.map((line, index) => (
              <div key={index} className={clsx('whitespace-pre-wrap break-all', entry.error ? 'text-red-400' : 'text-zinc-300')}>
                {line || ' '}
              </div>
            ))}
          </div>
        ))}
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-emerald-400">{prompt}</span>
          <input
            ref={inputRef}
            value={input}
            maxLength={CONSOLE_MAX_LINE}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            aria-label={`Konsole von ${server.name}`}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 font-mono text-xs text-zinc-100 caret-emerald-400 outline-none focus:ring-0"
          />
          {busy && <span className="animate-pulse text-zinc-500">…</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {SUGGESTIONS.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            disabled={busy}
            onClick={() => void submit(suggestion)}
            className="rounded border border-panel-200 px-2 py-0.5 font-mono text-2xs text-panel-600 hover:bg-panel-100 disabled:opacity-50 dark:border-panel-700 dark:text-panel-300 dark:hover:bg-panel-800"
          >
            {suggestion}
          </button>
        ))}
      </div>
    </div>
  );
}

function banner(server: Server): Entry[] {
  return [
    {
      id: ++entryCounter,
      input: null,
      output: [`Serielle Konsole (IPMI SOL) zu ${server.name}. „help“ zeigt die verfügbaren Befehle.`, ''],
    },
  ];
}
